import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  selectClinicId,
  selectClinicProfile,
  selectTherapistId,
  selectCanAccessFinance,
  selectIsAdmin,
  selectIsNutritionClinic,
} from "../../../store/slices/authSlice";
import { useGetPendingActionAppointmentsQuery } from "../../../store/services/calendarApi";
import { useGetTasksQuery } from "../../../store/services/tasksApi";
import { useGetNotificationsQuery } from "../../../store/services/notificationsApi";
import {
  DEFAULT_POLLING_INTERVAL,
  useVisibilityRefresh,
} from "../../../hooks/useRealtimeQuery";

// Open tasks ordered by status urgency (mirrors CollapsibleSections).
const STATUS_SORT_ORDER = {
  in_progress: 0,
  waiting: 1,
  todo: 2,
  done: 3,
};

/**
 * useCatchUpItems
 * --------------------------------------------------------------
 * Aggregates the three "catch up" categories the CATCH UP card acts on:
 * pending meeting actions, unread notifications, and open tasks.
 *
 * Returns:
 *   - counts:   { meetings, notifications, tasks }
 *   - items[]:  ordered card list (meetings → notifications → tasks),
 *               each { type: 'meeting'|'notification'|'task', id, data }
 *   - total:    items.length
 *   - isLoading
 *
 * Query setups (params, polling, filters) mirror the now-removed
 * CollapsibleSections accordion so behaviour is unchanged.
 */
export default function useCatchUpItems() {
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const clinicProfile = useSelector(selectClinicProfile);
  const canAccessFinance = useSelector(selectCanAccessFinance);
  const isAdmin = useSelector(selectIsAdmin);
  const isNutritionClinic = useSelector(selectIsNutritionClinic);

  const allowFinanceVal = clinicProfile?.therapistPermissions?.allowFinanceAccess;
  const financeAccessRestricted =
    !canAccessFinance ||
    (isAdmin && (allowFinanceVal === false || allowFinanceVal === null));

  // --- Pending meeting actions ---
  const {
    data: pendingAppointments = [],
    isLoading: isLoadingPending,
    refetch: refetchPending,
  } = useGetPendingActionAppointmentsQuery(
    { clinicId, therapistId, isAdmin, financeAccessRestricted },
    {
      // Nutrition clinics have no appointment-stage loop, so CATCH UP has
      // nothing to catch up on here and must not fire this query at all.
      skip: !clinicId || !therapistId || isNutritionClinic,
      pollingInterval: DEFAULT_POLLING_INTERVAL,
    }
  );

  // --- Notifications ---
  const { data: notificationsData, refetch: refetchNotifications } =
    useGetNotificationsQuery(
      { clinicId, therapistId, pageSize: 20 },
      { skip: !clinicId || !therapistId, pollingInterval: DEFAULT_POLLING_INTERVAL }
    );

  // --- Tasks (personal + clinic-assigned-to-me) ---
  const { data: personalTasks = [], refetch: refetchPersonalTasks } =
    useGetTasksQuery(
      { clinicId, scope: "personal", therapistId },
      { skip: !clinicId || !therapistId, pollingInterval: DEFAULT_POLLING_INTERVAL }
    );

  const { data: clinicTasks = [], refetch: refetchClinicTasks } = useGetTasksQuery(
    { clinicId, scope: "clinic" },
    { skip: !clinicId, pollingInterval: DEFAULT_POLLING_INTERVAL }
  );

  useVisibilityRefresh(refetchPending);
  useVisibilityRefresh(refetchNotifications);
  useVisibilityRefresh(refetchPersonalTasks);
  useVisibilityRefresh(refetchClinicTasks);

  // Appointments that still need a step (approval / summary / payment).
  // Mirrors the base "auto-determine" branch of getCurrentStep — payment only
  // counts when the therapist can access finance.
  const meetingItems = useMemo(() => {
    return pendingAppointments
      .filter(
        (apt) =>
          apt.NotifyAboutThisMeetingApproval === true ||
          (apt.isTempCustomer !== true &&
            apt.NotifyAboutThisMeetingSummarize === true) ||
          (canAccessFinance && apt.NotifyAboutThisMeetingPaymentStatus === true)
      )
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
      .map((apt) => ({ type: "meeting", id: `meeting-${apt.id}`, data: apt }));
  }, [pendingAppointments, canAccessFinance]);

  const notificationItems = useMemo(() => {
    return (notificationsData?.notifications || [])
      .filter((n) => !n.readBy?.includes(therapistId))
      .map((n) => ({ type: "notification", id: `notification-${n.id}`, data: n }));
  }, [notificationsData, therapistId]);

  const taskItems = useMemo(() => {
    const myClinicTasks = clinicTasks.filter((t) => t.assignedTo === therapistId);
    return [...personalTasks, ...myClinicTasks]
      .filter((t) => t.status !== "done")
      .sort(
        (a, b) =>
          (STATUS_SORT_ORDER[a.status] ?? 99) - (STATUS_SORT_ORDER[b.status] ?? 99)
      )
      .map((t) => ({ type: "task", id: `task-${t.id}`, data: t }));
  }, [personalTasks, clinicTasks, therapistId]);

  const items = useMemo(
    () => [...meetingItems, ...notificationItems, ...taskItems],
    [meetingItems, notificationItems, taskItems]
  );

  const counts = useMemo(
    () => ({
      meetings: meetingItems.length,
      notifications: notificationItems.length,
      tasks: taskItems.length,
    }),
    [meetingItems.length, notificationItems.length, taskItems.length]
  );

  return {
    counts,
    items,
    total: items.length,
    isLoading: isLoadingPending,
  };
}
